import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { ChevronLeftIcon, Eye, EyeClosed, LoaderIcon } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerDescription,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from "@/components/ui/drawer";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { getCsrfToken } from "@/lib/csrf";
import { cn, generateInitials } from "@/lib/utils";
import type { PatientIndexGlobalPageProps } from "@/pages/AdminPortal/Patient/Index";

const FORM_SCHEMA = z.object({
	adminEmail: z.string().email(),
	newEmail: z
		.string()
		.min(1, "New email is required")
		.email("Invalid email format"),
	confirmPassword: z.string().min(1, "Password confirmation is required"),
});
type FormSchema = z.infer<typeof FORM_SCHEMA>;

const STEPS = [1, 2] as const;
type Step = (typeof STEPS)[number];

export interface ChangeEmailDrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	userId: string;
	patientName: string;
	currentEmail: string | null | undefined;
}

export default function ChangeEmailDrawer({
	open,
	onOpenChange,
	userId,
	patientName,
	currentEmail,
}: ChangeEmailDrawerProps) {
	const { t: td } = useTranslation("translation", { keyPrefix: "components" });
	const { t: tp } = useTranslation("patients", { keyPrefix: "change_email" });
	const { props: globalProps } = usePage<PatientIndexGlobalPageProps>();

	const [step, setStep] = useState<Step>(1);
	const [isLoading, setIsLoading] = useState(false);
	const [passwordVisibility, setPasswordVisibility] = useState(false);

	const adminEmail = globalProps.auth.currentUser?.user.email || "";

	const form = useForm<FormSchema>({
		resolver: zodResolver(FORM_SCHEMA),
		defaultValues: {
			adminEmail,
			newEmail: "",
			confirmPassword: "",
		},
		mode: "onBlur",
	});

	async function onSubmit(values: FormSchema) {
		setIsLoading(true);
		try {
			const csrfToken = getCsrfToken();

			const response = await fetch(`/admin-portal/users/${userId}/email`, {
				method: "PUT",
				headers: {
					"Content-Type": "application/json",
					Accept: "application/json",
					"X-CSRF-Token": csrfToken,
				},
				body: JSON.stringify({
					admin_email: values.adminEmail,
					new_email: values.newEmail,
					confirm_password: values.confirmPassword,
				}),
			});

			const data = await response.json();
			if (response.ok && data.success) {
				toast.success(data.message || tp("messages.success"));
				onOpenChange(false);
				setStep(1);
				form.reset();
				router.reload();
			} else {
				toast.error(data.message || tp("messages.error"));
			}
		} catch (_error) {
			toast.error(tp("messages.error"));
		} finally {
			setIsLoading(false);
		}
	}

	function handleNext() {
		const isValid = form.trigger(["newEmail"]);
		isValid.then((valid) => {
			if (valid) setStep(2);
		});
	}

	const initials = generateInitials(patientName);

	return (
		<Drawer
			open={open}
			onOpenChange={(value) => {
				if (!value) {
					setStep(1);
					form.reset();
				}
				onOpenChange(value);
			}}
		>
			<DrawerContent
				onInteractOutside={(event) => {
					event.preventDefault();
				}}
				className="flex flex-col rounded-t-[10px] mt-24 max-h-[90%] fixed bottom-0 left-0 right-0 outline-none bg-card"
			>
				<div className="flex-1 overflow-y-auto overflow-x-hidden">
					<div className="w-full max-w-sm mx-auto min-w-0">
						<DrawerHeader>
							<DrawerTitle>{tp("title")}</DrawerTitle>
							<DrawerDescription>{tp("description")}</DrawerDescription>
						</DrawerHeader>

						<div className="flex items-center gap-2 px-4 pb-2">
							{STEPS.map((s) => (
								<div
									key={s}
									className={cn(
										"flex-1 h-1 rounded-full transition-colors",
										step >= s ? "bg-primary" : "bg-muted",
									)}
								/>
							))}
						</div>

						<Form {...form}>
							<form
								onSubmit={form.handleSubmit(onSubmit)}
								className="grid gap-4 p-4"
							>
								{step === 1 && (
									<>
										<div className="flex items-center gap-4 p-4 border rounded-xl bg-input/50 border-border/50">
											<div className="flex items-center justify-center border rounded-full size-10 bg-background shrink-0">
												<span className="text-xs font-bold text-muted-foreground uppercase">
													{initials}
												</span>
											</div>
											<div className="min-w-0 space-y-0.5">
												<h3 className="text-sm font-bold text-foreground truncate uppercase">
													{patientName}
												</h3>
												<p className="text-xs text-muted-foreground/75 truncate">
													{currentEmail || tp("overview.no_email_registered")}
												</p>
											</div>
										</div>

										<Separator />

										<FormField
											control={form.control}
											name="newEmail"
											render={({ field }) => (
												<FormItem>
													<FormLabel>{tp("fields.new_email.label")}</FormLabel>
													<FormControl>
														<Input
															{...field}
															type="email"
															autoComplete="email"
															placeholder={tp("fields.new_email.placeholder")}
															className="shadow-inner bg-input"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<DrawerFooter className="gap-4 px-0 pb-0">
											<Button type="button" size="lg" onClick={handleNext}>
												{tp("steps.next")}
											</Button>

											<DrawerClose asChild>
												<Button
													variant="outline"
													type="button"
													size="lg"
													className="shadow-none bg-card"
												>
													{td("modal.cancel")}
												</Button>
											</DrawerClose>
										</DrawerFooter>
									</>
								)}

								{step === 2 && (
									<>
										<button
											type="button"
											className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors -mt-1"
											onClick={() => setStep(1)}
										>
											<ChevronLeftIcon className="size-4" />
											<span>{tp("steps.back")}</span>
										</button>

										<Separator />

										<FormField
											control={form.control}
											name="adminEmail"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{tp("fields.admin_email.label")}
													</FormLabel>
													<FormControl>
														<Input
															{...field}
															type="email"
															readOnly
															placeholder={tp("fields.admin_email.placeholder")}
															className="shadow-inner bg-input"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="confirmPassword"
											render={({ field }) => (
												<FormItem>
													<FormLabel>
														{tp("fields.confirm_password.label")}
													</FormLabel>
													<FormControl>
														<div className="relative">
															<Input
																{...field}
																type={passwordVisibility ? "text" : "password"}
																autoComplete="current-password"
																placeholder={tp(
																	"fields.confirm_password.placeholder",
																)}
																className="pr-8 shadow-inner bg-input"
															/>
															<Button
																type="button"
																variant="ghost"
																size="icon"
																className="absolute -translate-y-1/2 right-1 top-1/2 h-7 w-7 text-muted-foreground"
																onClick={() =>
																	setPasswordVisibility(!passwordVisibility)
																}
															>
																{!passwordVisibility ? (
																	<Eye className="size-4 opacity-75" />
																) : (
																	<EyeClosed className="size-4 opacity-75" />
																)}
																<span className="sr-only">
																	Toggle Password Visibility
																</span>
															</Button>
														</div>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<DrawerFooter className="gap-4 px-0 pb-0">
											<Button
												type="submit"
												size="lg"
												disabled={isLoading || !form.formState.isValid}
											>
												{isLoading ? (
													<>
														<LoaderIcon className="animate-spin" />
														<span>{td("modal.wait")}</span>
													</>
												) : (
													<span>{td("modal.save")}</span>
												)}
											</Button>

											<DrawerClose asChild>
												<Button
													disabled={isLoading}
													variant="outline"
													type="button"
													size="lg"
													className="shadow-none bg-card"
												>
													{td("modal.cancel")}
												</Button>
											</DrawerClose>
										</DrawerFooter>
									</>
								)}
							</form>
						</Form>
					</div>
				</div>
			</DrawerContent>
		</Drawer>
	);
}
